import { ReportBatchValidationMessage } from 'constants/reports';
import { isDayLockedForUser } from 'redux/modules/dayProperties';
import { getDayProperties } from 'redux/modules/dayProperties.selectors';
import { getHourStates } from 'redux/modules/hourStates';
import { getTags } from 'redux/modules/tags.selectors';
import { createSelector } from 'reselect';
import { HourLockedReasons } from 'types/hour';
import type { Tag } from 'types/tag';
import { getCommonTagsForSelection } from 'utils/superreports';
import { notNull } from 'utils/typesafe';
import { getCurrentAccount } from './accounts';
import { getClients } from './clients';
import { getHourEntities } from './hours';
import {
  getAllLoadedProjects,
  getAllProjectsHaveBeenFetched,
  getProjectVariantMap,
  getProjectXhrStatus,
} from './projects';
import { getReportSelection, getXhrStatusUiState } from './superreports';
import { getUser, getUserPermissions } from './user';

const getTagsForSelection = createSelector(
  [getTags, getHourEntities, getProjectVariantMap, getReportSelection],
  (tags, hourEntities, projectMap, selection) => {
    const { hourIds } = selection;
    if (hourIds.length === 0 || !hourEntities) {
      return tags;
    }
    const projectIds = hourIds.map(id => hourEntities[id]?.project).filter(notNull);
    return getCommonTagsForSelection(tags, projectIds, projectMap);
  }
);

export const getBatchSelectionMetadata = createSelector(
  [getReportSelection, getHourEntities, getDayProperties],
  (selection, hourEntities, dayProperties) => {
    let lockedHourCount = 0;
    let billedHourCount = 0;
    let readonlyHoursCount = 0;
    let hasBillableHours = false;
    let hasNonBillableHours = false;
    let hasPlannedTime = false;
    let hasLoggedTime = false;
    let hasDrafts = false;
    let hasHoursOnLockedDays = false;

    const selectedUserIds = selection.hourIds.reduce((selectedUserIds, hourId) => {
      const hour = hourEntities[hourId];
      if (hour) {
        if (hour.locked && hour.locked_reason === HourLockedReasons.Locked) lockedHourCount++;
        if (hour.billed) billedHourCount++;
        if (!hour.manage) readonlyHoursCount++;
        hasBillableHours ||= hour.billable;
        hasNonBillableHours ||= !hour.billable;
        hasPlannedTime ||= !!hour.estimated_duration.total_minutes;
        hasLoggedTime ||= !!hour.duration.total_minutes;
        hasDrafts ||= !!hour.draft;
        selectedUserIds.add(hour.user.id);
        if (isDayLockedForUser(hour.user.id, dayProperties[hour.day])) {
          hasHoursOnLockedDays = true;
        }
      }
      return selectedUserIds;
    }, new Set<number>());

    return {
      allHoursArePlanned: hasPlannedTime && !hasLoggedTime && !hasDrafts,
      allHoursAreLocked: lockedHourCount === selection.hourIds.length,
      hasLockedHours: lockedHourCount > 0,
      allHoursAreBilled: billedHourCount === selection.hourIds.length,
      allHoursAreReadonly: readonlyHoursCount === selection.hourIds.length,
      hasReadonlyHours: readonlyHoursCount > 0,
      hasBilledHours: billedHourCount > 0,
      hasBillableHours,
      hasNonBillableHours,
      hasHoursOnLockedDays,
      selectedUserIds: Array.from(selectedUserIds),
    };
  }
);

const getCanUpdateProject = createSelector(
  [getBatchSelectionMetadata, getReportSelection],
  (
    { hasLockedHours, selectedUserIds, hasHoursOnLockedDays },
    { labelIds }
  ): { canUpdateProject: boolean; cannotUpdateProjectReason?: string } => {
    if (hasHoursOnLockedDays) {
      return {
        canUpdateProject: false,
        cannotUpdateProjectReason: ReportBatchValidationMessage.CannotUpdateLockedDay,
      };
    }

    if (hasLockedHours) {
      return {
        canUpdateProject: false,
        cannotUpdateProjectReason: ReportBatchValidationMessage.CannotUpdateLocked,
      };
    }

    if (selectedUserIds.length > 1) {
      return {
        canUpdateProject: false,
        cannotUpdateProjectReason:
          'You may not change the project of entries from multiple users at the same time.',
      };
    }

    if (labelIds.length > 1) {
      return {
        canUpdateProject: false,
        cannotUpdateProjectReason:
          'You may not make changes to both project and tags at the same time.',
      };
    }

    return { canUpdateProject: true };
  }
);

const getCanUpdateTags = createSelector(
  [getBatchSelectionMetadata, getReportSelection, getTagsForSelection],
  (
    { hasLockedHours, hasHoursOnLockedDays },
    { projectId },
    tags
  ): {
    canUpdateTags: boolean;
    cannotUpdateTagsReason?: string;
    tags: Array<Tag>;
  } => {
    if (hasHoursOnLockedDays) {
      return {
        canUpdateTags: false,
        cannotUpdateTagsReason: ReportBatchValidationMessage.CannotUpdateLockedDay,
        tags,
      };
    }

    if (hasLockedHours) {
      return {
        canUpdateTags: false,
        cannotUpdateTagsReason: ReportBatchValidationMessage.CannotUpdateLocked,
        tags,
      };
    }

    if (!tags.length) {
      return {
        canUpdateTags: false,
        cannotUpdateTagsReason:
          'You may not make changes to entries from projects with no tags in common.',
        tags,
      };
    }

    if (projectId) {
      return {
        canUpdateTags: false,
        cannotUpdateTagsReason:
          'You may not make changes to both project and tags at the same time.',
        tags,
      };
    }

    return { canUpdateTags: true, tags };
  }
);

export const getCanUpdateState = createSelector(
  [getBatchSelectionMetadata],
  ({
    hasHoursOnLockedDays,
  }): {
    canUpdateState: boolean;
    cannotUpdateStateReason?: string;
  } => {
    return {
      canUpdateState: !hasHoursOnLockedDays,
      cannotUpdateStateReason: hasHoursOnLockedDays
        ? ReportBatchValidationMessage.CannotUpdateLockedDay
        : undefined,
    };
  }
);

export const getReportBatchActionsData = createSelector(
  [
    getCurrentAccount,
    getReportSelection,
    getUser,
    getXhrStatusUiState,
    getBatchSelectionMetadata,
    getClients,
    getAllLoadedProjects,
    getAllProjectsHaveBeenFetched,
    getProjectXhrStatus,
    getCanUpdateProject,
    getCanUpdateTags,
    getCanUpdateState,
    getUserPermissions,
    getHourStates,
  ],
  (
    account,
    selection,
    user,
    xhrStatus,
    {
      selectedUserIds,
      hasBillableHours,
      hasNonBillableHours,
      allHoursArePlanned,
      allHoursAreReadonly,
      hasReadonlyHours,
      hasLockedHours,
      allHoursAreLocked,
      hasBilledHours,
      allHoursAreBilled,
    },
    clients,
    projects,
    allProjectsLoaded,
    { isLoading: isLoadingProjects },
    { canUpdateProject, cannotUpdateProjectReason },
    { canUpdateTags, cannotUpdateTagsReason, tags },
    { canUpdateState, cannotUpdateStateReason },
    userPermissions,
    states
  ) => {
    const { isSaving } = xhrStatus;
    const isDeleting = isSaving;
    const numSelectedHours = selection.hourIds.length;
    const validUpdateBatchAction =
      selection.billedState !== null ||
      (selection.labelIds.length > 0 && canUpdateTags) ||
      (selection.projectId !== null && canUpdateProject) ||
      selection.lockedState !== null ||
      typeof selection.stateId !== 'undefined';

    const countedEntryLabel = numSelectedHours === 1 ? 'entry' : 'entries';
    const affectedEntriesLabel = `${numSelectedHours} ${countedEntryLabel} `;
    const deleteConfirmMessage = `This will permanently delete ${numSelectedHours} ${countedEntryLabel} from your Timely workspace, not just this report.`;

    const selectedProject = selection.projectId
      ? projects.find(project => project.base.id === selection.projectId)
      : null;

    const selectedProjectBillableState = selectedProject?.fullProject?.billable;

    const [userId] = selectedUserIds;
    // With ALL_PROJECTS_LOGGING enabled, batch-updating entries to a
    // project the author is not a member of is fine: the backend auto-
    // joins the author on save (via the AutoJoinProject concern in
    // CreateHour / UpdateHour). Skip the warning and don't disable the
    // Update button in that case.
    const userIsNotInProject =
      !account.features.all_projects_logging &&
      canUpdateProject &&
      !!selectedProject &&
      !selectedProject.fullProject?.users?.find(user => user.user_id === userId);

    const updateActionDisabled =
      isSaving || isDeleting || !validUpdateBatchAction || isLoadingProjects || userIsNotInProject;

    const hasProjectBillableMismatch =
      !!selectedProject &&
      ((selectedProjectBillableState && hasNonBillableHours) ||
        (!selectedProjectBillableState && hasBillableHours));

    const showBatchLogPlannedTime = allHoursArePlanned && !!account.features.batch_log_planned_time;

    return {
      clients,
      currency: account.currency,
      projects,
      tags,
      selection,
      userId,
      states,
      ui: {
        isVisible: numSelectedHours > 0,
        isBillingDisabled: !account.features.billing,
        isUserAdmin: user.admin,
        canUpdateTags,
        cannotUpdateTagsReason,
        canUpdateState,
        cannotUpdateStateReason,
        updateLabel: isSaving ? 'Updating...' : 'Update',
        deleteLabel: isDeleting ? 'Deleting...' : 'Delete',
        deleteConfirmMessage,
        updateActionDisabled,
        canUpdateLocked: userPermissions.canUpdate.projects,
        deleteActionDisabled: hasLockedHours || isSaving || isDeleting,
        cannotDeleteReason: hasLockedHours ? 'You may not delete locked entries.' : undefined,
        affectedEntriesLabel,
        canUpdateProject,
        cannotUpdateProjectReason,
        allProjectsLoaded,
        isLoadingProjects,
        userIsNotInProject,
        hasProjectBillableMismatch,
        selectedProjectBillableState,
        showBatchLogPlannedTime,
        hasLockedHours,
        allHoursAreLocked,
        hasBilledHours,
        allHoursAreBilled,
        allHoursAreReadonly,
        hasReadonlyHours,
        hasHourStatesFeature: !!account.features.hour_states,
        userRoleId: user.role_id,
      },
    };
  }
);
